import React, { useState, useCallback, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useEstimatView,
  TimeProposalsSection,
  HomeVisitProposalsSection,
  CustomerHomeVisitRequest,
  BookedHomeVisitSection,
  BookedFollowupMeetingSection,
  EstimatActionSection,
  FollowupNotesSection,
  ConsultantApprovalButton,
  ConfirmationModal,
  MeetingBookingModal,
  FeedbackSubmitForm,
  NewFeedbackBanner,
  EstimatCategoryTable,
  SkissSection,
} from '../features/estimat';
import { FeedbackSection } from '../features/shared/feedback';
import {  hasAnyPublishedCustomerComment, hasPublishedCustomerCommentAfter } from '../features/estimat/utils/commentHelpers';
import ConfirmationModalCommon from '../components/common/ConfirmationModal';
import VersionsJamforelse from '../features/estimat/components/view/VersionsJamforelse';

const EstimatViewInnehall = ({ embedded = false, isPresentationMode = false, leadIdOverride, estimatIdOverride, onFeedbackSent, hideSkiss = false, onVisaVersion, jamforelseLage = false }) => {
  const { leadId, estimatId } = useParams();
  const [visaJamforelse, setVisaJamforelse] = useState(false);
  const navigate = useNavigate();
  const lid = leadIdOverride || leadId;
  const eid = estimatIdOverride || estimatId;

  // State för bekräftelsedialog för godkännande åt kund
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  // Flagga som triggar navigation till Upphandling + hembesöks-modal efter lyckat godkännande åt kund
  const [shouldOpenHomeVisitAfterApproval, setShouldOpenHomeVisitAfterApproval] = useState(false);

  // State för expanderbara sektioner (Offert-stil)
  const [expandedOversikt, setExpandedOversikt] = useState(true);
  const [expandedProjektstart, setExpandedProjektstart] = useState(true);
  const [expandedInnehall, setExpandedInnehall] = useState(true);
  const [expandedVersionshistorik, setExpandedVersionshistorik] = useState(true);

  // 🎯 FACADE HOOK - All state management kapslad här!
  const { data, derived, actions, status, setters } = useEstimatView(lid, eid, embedded, onFeedbackSent);

  // Destrukturera allt vi behöver från facade hook
  const {
    lead, estimatData, user,
    comments, savedComments, commentsRaw, showComments,
    isCustomerView,
    expandedSections, overallFeedback,
    budgetFeedback, prioriteringFeedback,
    wantsMeeting, meetingSlots,
      expandedFeedbackItems,
    newReplies, savedReplies,
    builderProposals, 
    procurementData,
    followupNotes,
    timeProposalReplyText,
    homeVisitReplyText,
  } = data;

  const { 
    loading, error, submitting,
    savingFollowupNotes, sendingTimeProposalReply, sendingHomeVisitReply, submittingHomeVisitProposals,
    followupNotesSaved, feedbackSent,
    showMeetingModal, showConfirmModal,
    showImportant, dismissedIntroKey,
    acceptingProposal, acceptingHomeVisitProposal,
    expandedTimeProposal, expandedHomeVisitProposal, expandedBookedHomeVisit, expandedBookedFollowupMeeting,
    showTimeProposalReply,
    showHomeVisitReply,
  } = status;

  const {
    handleCommentChange, toggleCommentField, handleSaveComment, handleSaveOverallFeedback, handleSaveBudgetFeedback, handleSavePrioriteringFeedback, handleDeleteComment, handleSaveByggkonsultComment, handleEditByggkonsultEntry,
    handleSendComment, handleSaveReply, handleSendAllReplies,
    handleCustomerMarkAsOK,
    handleCustomerUndoMarkAsOK,
    handleAcceptProposal, handleTimeProposalReply,
    handleAcceptHomeVisitProposal, handleHomeVisitReply, handleSubmitHomeVisitProposals,
    handleEstimatAction, handleConfirmAction,
    saveFollowupNotes, hasUnsavedChanges, isSavedLocally,
    formatDate, generateMeetingOptions,
    logActivity,
  } = actions;

  const {
    setOverallFeedback,
    setBudgetFeedback, setPrioriteringFeedback,
    setWantsMeeting, setMeetingSlots,
    setFollowupNotes, setFollowupNotesSaved, setExpandedSections, setExpandedFeedbackItems,
    setExpandedTimeProposal, setExpandedHomeVisitProposal,
    setExpandedBookedHomeVisit, setExpandedBookedFollowupMeeting,
    setShowMeetingModal, setShowConfirmModal,
    setShowImportant, setDismissedIntroKey,
    setNewReplies, 
    setShowTimeProposalReply, setShowHomeVisitReply,
    setTimeProposalReplyText, setHomeVisitReplyText,
  } = setters;

  const {  sectionTotals } = derived;

  // Läsläge: BK:s presentation, eller kunden som tittar på en tidigare version.
  // Döljer allt man kan svara, kommentera eller boka med.
  const arTidigareVersion = Boolean(estimatData?.arTidigareVersion);
  const lasLage = isPresentationMode || arTidigareVersion;

  // Callback för att öppna bekräftelsedialog istället för att godkänna direkt
  const handleApproveClick = useCallback(() => {
    setShowApproveConfirmModal(true);
  }, []);

  // Callback för att faktiskt godkänna efter bekräftelse
  const confirmApprove = useCallback(() => {
    setShowApproveConfirmModal(false);
    // Markera att vi ska öppna hembesöks-modalen direkt efter att godkännandet gått igenom
    if (!isCustomerView && !embedded) {
      setShouldOpenHomeVisitAfterApproval(true);
    }
    handleEstimatAction('approved');
  }, [handleEstimatAction, isCustomerView, embedded]);

  // När godkännandet väl syns i estimatData, navigera till Upphandling med hembesöks-valet öppet
  useEffect(() => {
    if (!shouldOpenHomeVisitAfterApproval) return;
    const isApproved =
      estimatData?.approvedByConsultant ||
      estimatData?.customerResponse?.action === 'approved' ||
      estimatData?.customerResponse?.status === 'approved' ||
      estimatData?.status === 'approved';
    if (isApproved) {
      setShouldOpenHomeVisitAfterApproval(false);
      navigate('/workspace/upphandling', {
        state: { highlightProcurementId: lid, openAction: 'hembesok' }
      });
    }
  }, [shouldOpenHomeVisitAfterApproval, estimatData, navigate, lid]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Laddar estimat...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">Fel: {error}</div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-gray-50"}>
      {!embedded && <Navbar currentUser={user} />}
      {/* Print styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            .no-print { display: none !important; }
            body { print-color-adjust: exact; }
          }
        `
      }} />

      {/* Header */}
      {!embedded && (
      <div className="bg-gray-50 border-b-2 border-gray-200 p-6">
        <div className={user?.role === 'byggkonsult' ? "container mx-auto px-4" : "max-w-4xl mx-auto"}>
          <div className="flex justify-between items-start">
            <div>
              {/* Tillbaka-knapp för byggkonsulter */}
              {!embedded && user?.role === 'byggkonsult' && (
                <button
                  onClick={() => navigate(`/estimat/${leadId}`)}
                  className="mb-3 flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Tillbaka till Estimat
                </button>
              )}
              
              {/* Tillbaka-knapp för kunder */}
              {!embedded && user?.role === 'kund' && (
                <button
                  onClick={() => navigate('/myprojects')}
                  className="mb-3 flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Tillbaka till Mina Projekt
                </button>
              )}
              
              <div className="flex items-center justify-between">
                <div>
                  {(() => {
                    return (
                      <h1 className="text-3xl font-bold text-gray-800 mb-1 flex items-center flex-wrap gap-3">
                        {(() => {
                          const version = Number(estimatData?.version || 1);
                          return `Estimat ${version.toFixed(1)}`;
                        })()}
                        {estimatData?.showForstudiePresentationToCustomer && (
                          <button
                            type="button"
                            onClick={() => window.open(
                              `/forstudie-presentation?lang=${estimatData?.forstudiePresentationSprak || 'sv'}`,
                              '_blank',
                              'noopener'
                            )}
                            className="inline-flex items-center gap-1.5 bg-leadme-pink hover:bg-leadme-pink-dark text-white px-3 py-1 rounded-full text-sm font-medium transition-colors"
                            title="Öppna förstudie-presentationen i nytt fönster"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Se förstudie-presentation
                          </button>
                        )}
                        {(() => {
                          // Använd procurementStatus först, sen vanlig status som fallback
                          const procStatus = estimatData?.procurementStatus;
                          const status = estimatData?.status;
                          
                          // Mappa procurement status till lämpliga badges
                          if (procStatus === 'Godkänt estimat') {
                            return <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">✓ Godkänt estimat</span>;
                          } else if (procStatus === 'Avböjt estimat' || procStatus === 'Nej tack') {
                            return <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">✗ Avböjt</span>;
                          } else if (procStatus === 'Feedback estimat') {
                            return <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">💬 Feedback estimat</span>;
                          } else if (procStatus === 'Uppföljning estimat') {
                            return <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">📅 Uppföljning bokad</span>;
                          } else if (procStatus === 'Skickat estimat') {
                            // Visa "Nytt estimat" för första versionen
                            if (estimatData?.version === 1 && estimatData?.sentAt) {
                              return <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">✨ Nytt estimat</span>;
                            }
                            return <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">📤 Skickat estimat</span>;
                          } else if (procStatus === 'Hembesök') {
                            return <span className="bg-indigo-500 text-white px-3 py-1 rounded-full text-sm font-medium">🏠 Hembesök</span>;
                          } else if (procStatus === 'Kalkyl') {
                            return <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">🧮 Kalkyl</span>;
                          } else if (procStatus === 'PU') {
                            return <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">📋 PU</span>;
                          } else if (procStatus === 'Offert') {
                            return <span className="bg-pink-500 text-white px-3 py-1 rounded-full text-sm font-medium">💰 Offert</span>;
                          } else if (procStatus === 'Projekt') {
                            return <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">🚀 Projekt</span>;
                          }
                          
                          // Fallback till vanlig status
                          if (status === 'approved') {
                            const approvalText = estimatData?.approvedByConsultant ? 
                              '✓ Godkänt (via byggkonsult)' : 
                              '✓ Godkänt';
                            return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">{approvalText}</span>;
                          } else if (status === 'declined') {
                            return <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">✗ Avböjt</span>;
                          } else if (status === 'feedback_received') {
                            return <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">💬 Feedback skickad</span>;
                          } else if (status === 'feedback_answered') {
                            return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">📝 Svar från byggkonsult</span>;
                          } else if (status === 'followup_booked') {
                            return <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">📅 Uppföljning bokad</span>;
                          } else if (status === 'sent') {
                            return <span className="bg-blue-400 text-white px-3 py-1 rounded-full text-sm font-medium">📤 Skickat</span>;
                          } else if (status === 'draft') {
                            return <span className="bg-gray-400 text-white px-3 py-1 rounded-full text-sm font-medium">📝 Utkast</span>;
                          } else {
                            // Helt nytt estimat - ingen status satt än
                            return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">✨ Nytt estimat</span>;
                          }
                        })()}
                      </h1>
                    );
                  })()}
                  {/* Beställaren först — föreningen för en BRF, personen annars.
                      Kontaktpersonen hamnar på raden under. */}
                  {(() => {
                    const ci = lead?.customerInfo || {};
                    const brfNamn = ci.kundtyp === 'brf' ? (ci.foreningsnamn || '').trim() : '';
                    const personNamn = (ci.name || '').trim();

                    if (brfNamn) {
                      return (
                        <>
                          <p className="text-lg text-gray-600">{brfNamn}</p>
                          {personNamn && (
                            <p className="text-sm text-gray-500 mb-2" title="Kontaktperson för föreningen">
                              {personNamn}
                            </p>
                          )}
                        </>
                      );
                    }
                    return personNamn ? (
                      <p className="text-lg text-gray-600 mb-2">{personNamn}</p>
                    ) : null;
                  })()}
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const pid = estimatData?.projectId || lead?.projectInfo?.projectId || '';
                      const ptype = (estimatData?.projectInfo?.projectType || lead?.projectInfo?.projectType || '').trim();
                      const projectLine = `Projekt ${pid ? `#${pid}` : '#XXXXX'}`;
                      const typeLine = `Projekttyp ${ptype || 'ej angiven'}`;
                      return (
                        <span className="mr-4">{projectLine} • {typeLine}</span>
                      );
                    })()}
                    {(() => {
                      const dateToShow = estimatData?.sentAt || estimatData?.createdAt || estimatData?.updatedAt;
                      const label = estimatData?.sentAt ? `Version ${estimatData?.version || 1}.0 skickat` : 'Datum';
                      return (<span className="mr-4">{label} {dateToShow ? formatDate(dateToShow) : ''}</span>);
                    })()}
                    {(() => {
                      // Visa godkännande-information om estimatet godkänts via byggkonsult
                      if (estimatData?.approvedByConsultant && estimatData?.approvedByConsultantAt) {
                        const approvalDate = formatDate(estimatData.approvedByConsultantAt);
                        return (
                          <span className="mr-2 sm:mr-4 text-green-600 font-medium text-xs sm:text-sm">
                            <span className="sm:hidden">Godkänt (BK) {approvalDate}</span>
                            <span className="hidden sm:inline">Estimat godkänt (via byggkonsult) {approvalDate}</span>
                          </span>
                        );
                      }
                      return null;
                    })()}
                    {(() => {
                      const pickName = (u) => {
                        if (!u) return '';
                        if (typeof u === 'string') return '';
                        return (`${u.firstName || ''} ${u.lastName || ''}`.trim()) || u.name || u.fullName || '';
                      };
                      const name = pickName(estimatData?.createdBy) || pickName(estimatData?.lastModifiedBy) || pickName(estimatData?.sentBy);
                      return name ? (<span>• Skapad av: {name}</span>) : null;
                    })()}
                  </div>
            </div>
            <div className="text-right">
                  {(() => {
                    try {
                      // Denna duplicerade status-badge är borttagen för att undvika förvirring
                      const hasConsultantReplies = estimatData?.byggkonsultResponses && Object.keys(estimatData.byggkonsultResponses).length > 0;
                      // Bara feedback som gäller DEN HÄR versionen. Ärvda
                      // kommentarer från föregående version tände annars badgen
                      // direkt när ett nytt estimat skickades.
                      const hasPublishedFeedback = hasPublishedCustomerCommentAfter(
                        estimatData?.customerResponse?.comments,
                        estimatData?.sentAt
                      );
                      if (hasConsultantReplies) {
                        return (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Svar från byggkonsult</span>
                        );
                      }
                      if (hasPublishedFeedback) {
                        return (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Feedback skickad</span>
                        );
                      }
                    } catch (_) {}
                    return null;
                  })()}
              </div>
              </div>
            </div>
            <div className="text-right"></div>
          </div>
        </div>
      </div>
      )}

      {/* Content */}
      <div className={embedded ? "p-0" : (user?.role === 'byggkonsult' ? "container mx-auto px-4 py-6" : "max-w-4xl mx-auto p-6")}>

        {/* Ny feedback redo att skickas — visas högst upp för kunder med opublicerade kommentarer */}
        {!lasLage && (
          <NewFeedbackBanner
            estimatData={estimatData}
            savedComments={savedComments}
            isCustomerView={isCustomerView}
            submitting={submitting}
            onSendFeedback={() => handleSendComment('', '')}
          />
        )}

        {/* Teams-genomgång — HÖGST UPP när kunden lämnat sin första feedback och inte
            begärt eller bokat möte. Längst ner missade kunderna att det gick att önska. */}
        {!lasLage && isCustomerView && (() => {
          const hasCustomerFeedback = estimatData?.customerResponse?.overallFeedback?.length > 0 || estimatData?.customerFeedback?.length > 0;
          const hasAnyCustomerComments = hasAnyPublishedCustomerComment(estimatData?.customerResponse?.comments);
          const isApproved = estimatData?.status === 'approved' || estimatData?.approvedByConsultant;
          const isDeclined = estimatData?.status === 'declined';

          // Kontrollera om kund redan begärt möte
          const hasMeetingRequest = Boolean(estimatData?.customerResponse?.comments?.__meetingRequest__);
          const hasProposedTimes = Boolean(
            estimatData?.estimatFollowup?.customerResponse?.proposals &&
            estimatData.estimatFollowup.customerResponse.proposals.length > 0
          );
          const hasMeetingBooked = Boolean(
            estimatData?.estimatFollowup?.teamsJoinUrl ||
            estimatData?.estimatFollowup?.meetingAt ||
            estimatData?.estimatFollowup?.selectedSlot
          );

          // Visa endast om:
          // 1. Feedback har skickats (hasCustomerFeedback eller hasAnyCustomerComments)
          // 2. Kunden inte redan begärt möte
          // 3. Inget möte är bokat
          // 4. Estimatet inte är godkänt/avböjt
          const hasSentFeedback = hasCustomerFeedback || hasAnyCustomerComments || feedbackSent;
          const canRequestMeeting = hasSentFeedback && !hasMeetingRequest && !hasProposedTimes && !hasMeetingBooked && !isApproved && !isDeclined;

          if (!canRequestMeeting) return null;

          return (
            <div id="feedback-section" className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-4 sm:p-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">Vill ni ha en genomgång via Teams?</h3>
                      <p className="text-xs text-gray-500">Vi bokar ett kort möte för att gå igenom estimatet och svara på frågor.</p>
                    </div>
                  </div>
                  <div className="mt-3 pl-13">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                      onClick={() => {
                        setWantsMeeting(true);
                        setShowMeetingModal(true);
                      }}
                    >
                      + Föreslå mötestider
                    </button>
                    {Array.isArray(meetingSlots) && meetingSlots.length > 0 && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">Valda tider: {meetingSlots.join(', ')}</p>
                        <button
                          onClick={() => handleEstimatAction('feedback')}
                          disabled={submitting}
                          className="mt-2 bg-leadme-pink hover:bg-leadme-pink-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Skickar...' : 'Skicka mötesförfrågan'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tidigare version i läsläge — kunden ska aldrig tro att det är den aktuella.
            I jämförelsen står versionen redan i kolumnens rubrik. */}
        {arTidigareVersion && !jamforelseLage && (
          <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-900">
              Du tittar på <strong>version {Number(estimatData.version || 1).toFixed(1)}</strong>
              {(() => {
                const sent = (estimatData.tidigareVersioner || []).find(v => String(v._id) === String(estimatData._id))?.sentAt;
                return sent ? `, skickad ${new Date(sent).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' })}` : '';
              })()}
              . Den är ersatt av en nyare version och visas bara för läsning.
            </p>
            <button
              type="button"
              onClick={() => onVisaVersion?.(estimatData.senasteVersion?._id || null)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
            >
              Gå till senaste versionen →
            </button>
          </div>
        )}

        {/* Tidigare skickade versioner — bara när BK slagit på det */}
        {isCustomerView && !jamforelseLage && (estimatData?.tidigareVersioner || []).length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h2 className="text-sm font-semibold text-gray-900">Tidigare versioner</h2>
              <button
                type="button"
                onClick={() => setVisaJamforelse(true)}
                className="inline-flex items-center gap-1.5 text-sm text-leadme-pink hover:text-leadme-pink-dark font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Jämför versioner
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ...(arTidigareVersion && estimatData.senasteVersion
                  ? [{ ...estimatData.senasteVersion, senaste: true }]
                  : []),
                ...estimatData.tidigareVersioner
              ].map(v => {
                const aktiv = String(v._id) === String(estimatData._id);
                return (
                  <button
                    key={v._id}
                    type="button"
                    disabled={aktiv}
                    onClick={() => onVisaVersion?.(v._id)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      aktiv
                        ? 'bg-gray-800 text-white border-gray-800 cursor-default'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {v.senaste ? `Senaste (${Number(v.version || 1).toFixed(1)})` : `Version ${Number(v.version || 1).toFixed(1)}`}
                    {!v.senaste && v.sentAt && (
                      <span className={`ml-1.5 text-xs ${aktiv ? 'text-gray-300' : 'text-gray-400'}`}>
                        {new Date(v.sentAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Versionshistorik – visar kommentarer för alla versioner */}
        {estimatData?.versionHistory && estimatData.versionHistory.length > 0 && (() => {
          const currentVersion = Number(estimatData.version || 1);

          // Filtrera versioner som har kommentarer och sortera (nyast först)
          const versionsWithComments = estimatData.versionHistory
            .filter(v => v.comment && v.comment.trim())
            .sort((a, b) => Number(b.version) - Number(a.version));

          if (versionsWithComments.length === 0) return null;

          // Hitta kommentar för aktuell version
          const currentVersionEntry = versionsWithComments.find(
            v => Number(v.version).toFixed(1) === currentVersion.toFixed(1)
          );

          // Tidigare versioners kommentarer (exkluderar aktuell version)
          const previousVersionsWithComments = versionsWithComments.filter(
            v => Number(v.version).toFixed(1) !== currentVersion.toFixed(1)
          );

          return (
            <div className="mb-6 bg-white rounded-lg shadow-sm border border-blue-200">
              {/* Accordion header - klickbar */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer select-none bg-blue-50 hover:bg-blue-100 transition-colors rounded-t-lg"
                onClick={() => setExpandedVersionshistorik(!expandedVersionshistorik)}
                role="button"
                tabIndex={0}
                aria-expanded={expandedVersionshistorik}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedVersionshistorik(!expandedVersionshistorik);
                  }
                }}
              >
                <h2 className="text-lg font-semibold text-blue-900">
                  📋 Versionshistorik
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-blue-800 font-medium">
                    Version {currentVersion.toFixed(1)}
                  </span>
                  <svg
                    className={`w-5 h-5 text-blue-600 transition-transform duration-200 ${expandedVersionshistorik ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Accordion content - expanderbar */}
              {expandedVersionshistorik && (
                <div className="p-4 bg-blue-50 border-t border-blue-200">
                  {/* Aktuell versions kommentar (markerad) */}
                  {currentVersionEntry && (
                    <div className="bg-white border border-blue-200 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-semibold text-blue-900 mb-2">Nytt i Version {currentVersion.toFixed(1)}</h3>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{currentVersionEntry.comment}</p>
                      <p className="text-xs text-blue-600 mt-3">
                        — {currentVersionEntry.sentBy ? `${currentVersionEntry.sentBy.firstName || ''} ${currentVersionEntry.sentBy.lastName || ''}`.trim() : 'Byggkonsult'}, {new Date(currentVersionEntry.sentAt).toLocaleDateString('sv-SE')}
                      </p>
                    </div>
                  )}

                  {/* Tidigare versioners kommentarer */}
                  {previousVersionsWithComments.length > 0 && (
                    <div className="space-y-3">
                      {currentVersionEntry && (
                        <h3 className="text-sm font-medium text-blue-800 mb-2">Tidigare versioner</h3>
                      )}
                      {previousVersionsWithComments.map((versionEntry, index) => {
                        const consultantName = versionEntry.sentBy
                          ? `${versionEntry.sentBy.firstName || ''} ${versionEntry.sentBy.lastName || ''}`.trim()
                          : 'Byggkonsult';

                        return (
                          <div key={index} className="bg-white border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-900">Version {Number(versionEntry.version).toFixed(1)}</span>
                              <span className="text-xs text-blue-600">{new Date(versionEntry.sentAt).toLocaleDateString('sv-SE')}</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{versionEntry.comment}</p>
                            <p className="text-xs text-blue-600 mt-2">— {consultantName}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Intro-meddelande från BK — visas högst upp, dismissable per unikt meddelande, auto-hide efter first feedback */}
        {(() => {
          const intro = estimatData?.customerIntroMessage;
          if (!isCustomerView || arTidigareVersion || !intro?.text) return null;
          if (estimatData?.customerResponse?.responseDate) return null;
          const introKey = intro.createdAt ? new Date(intro.createdAt).toISOString() : 'no-timestamp';
          if (dismissedIntroKey === introKey) return null;
          return (
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg mb-4 relative">
            <button
              aria-label="Stäng meddelande"
              title="Stäng"
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => { try { localStorage.setItem('hide_estimat_intro_key', introKey); } catch (_) {}; setDismissedIntroKey(introKey); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="text-sm text-gray-800 whitespace-pre-wrap pr-6">
              {estimatData.customerIntroMessage.text.split(/(_[^_]+_)/g).map((part, idx) =>
                part.startsWith('_') && part.endsWith('_') && part.length > 2
                  ? <em key={idx} className="italic">{part.slice(1, -1)}</em>
                  : <React.Fragment key={idx}>{part}</React.Fragment>
              )}
            </div>
          </div>
          );
        })()}

        {/* Viktigt-info + dismiss */}
        {showImportant && (
            <div className="bg-gray-100 border border-gray-200 p-4 rounded-lg mb-4 relative">
                <button
                aria-label="Stäng viktig information"
                title="Stäng"
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                onClick={() => { try { localStorage.setItem('hide_estimat_important', '1'); } catch (_) {}; setShowImportant(false); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                </button>
              <div className="text-sm text-gray-700">
            <p className="mb-2">
              <strong>Viktigt:</strong> Syftet med denna förenklade kalkyl/estimat är att ge en snabb, initial överblick av kostnader i ert planerade projekt. 
              Den kan hjälpa er genom att i tidigt skede ge indikation ifall projektet matchar budget samt att vi kan använda den som diskussionsunderlag 
              då vi hjälper er vidare med upphandling av projektet.
            </p>
            <p>
              Det är alltså ingen offert och ni kan på inget sätt se dessa priser som bindande gentemot oss eller våra samarbetspartners. 
              Alla priser är inklusive mervärdesskatt (moms).
            </p>
            {/* Kundvyn ska inte visa byggkonsultens kontakt här */}
              </div>
            </div>
        )}

        {/* Bokad hembesökstid - döljs i presentationsläge */}
        {!lasLage && (
          <BookedHomeVisitSection
            procurementData={procurementData}
            expanded={expandedBookedHomeVisit}
            onToggle={() => setExpandedBookedHomeVisit(!expandedBookedHomeVisit)}
          />
        )}

        {/* Kund föreslår hembesökstider - döljs i presentationsläge */}
        {!lasLage && user?.role !== 'byggkonsult' && estimatData?.customerResponse?.action === 'approved' && (
          <CustomerHomeVisitRequest
            procurementData={procurementData}
            onSubmitProposals={handleSubmitHomeVisitProposals}
            submitting={submittingHomeVisitProposals}
          />
        )}

        {/* Hembesöksförslag från byggkonsult - döljs i presentationsläge */}
        {!lasLage && (
          <HomeVisitProposalsSection
            procurementData={procurementData}
            acceptingHomeVisitProposal={acceptingHomeVisitProposal}
            onAcceptProposal={handleAcceptHomeVisitProposal}
            showHomeVisitReply={showHomeVisitReply}
            homeVisitReplyText={homeVisitReplyText}
            onHomeVisitReply={handleHomeVisitReply}
            sendingHomeVisitReply={sendingHomeVisitReply}
            setShowHomeVisitReply={setShowHomeVisitReply}
            setHomeVisitReplyText={setHomeVisitReplyText}
            expandedProposal={expandedHomeVisitProposal}
            onToggleProposal={() => setExpandedHomeVisitProposal(!expandedHomeVisitProposal)}
          />
        )}

        {/* Bokat uppföljningsmöte - döljs i presentationsläge */}
        {!lasLage && (
          <BookedFollowupMeetingSection
            estimatData={estimatData}
            expanded={expandedBookedFollowupMeeting}
            onToggle={() => setExpandedBookedFollowupMeeting(!expandedBookedFollowupMeeting)}
          />
        )}

        {/* Tidsförslag från byggkonsult - döljs i presentationsläge */}
        {!lasLage && (
          <TimeProposalsSection
            builderProposals={builderProposals}
            acceptingProposal={acceptingProposal}
            onAcceptProposal={handleAcceptProposal}
            showTimeProposalReply={showTimeProposalReply}
            timeProposalReplyText={timeProposalReplyText}
            onTimeProposalReply={handleTimeProposalReply}
            sendingTimeProposalReply={sendingTimeProposalReply}
            setShowTimeProposalReply={setShowTimeProposalReply}
            setTimeProposalReplyText={setTimeProposalReplyText}
            expanded={expandedTimeProposal}
            onToggle={() => setExpandedTimeProposal(!expandedTimeProposal)}
          />
        )}

         {/* Feedback-sektion för kunden - döljs i presentationsläge */}
         {/* Visa ENDAST om kunden har ställt frågor som byggkonsulten svarat på (inte proaktiva anteckningar) */}
         {!lasLage && isCustomerView && (() => {
           // Filtrera ut endast byggkonsult-svar som har motsvarande kundkommentar
           const customerCommentKeys = Object.keys(estimatData?.customerResponse?.comments || {});
           const relevantByggkonsultResponses = {};

           if (estimatData?.byggkonsultResponses) {
             Object.keys(estimatData.byggkonsultResponses).forEach(key => {
               // Visa bara om kunden har en kommentar på samma rad
               if (customerCommentKeys.includes(key)) {
                 relevantByggkonsultResponses[key] = estimatData.byggkonsultResponses[key];
               }
             });
           }

           // Visa sektionen endast om det finns relevanta svar
           if (Object.keys(relevantByggkonsultResponses).length === 0) return null;

           return (
             <FeedbackSection
               incomingComments={relevantByggkonsultResponses}
               myResponses={estimatData?.customerResponse?.comments || {}}
               formData={estimatData?.estimat || {}}
               expandedFeedbackItems={expandedFeedbackItems}
               setExpandedFeedbackItems={setExpandedFeedbackItems}
               newResponses={newReplies}
               savedResponses={savedReplies}
               onResponseChange={(commentKey, value) => setNewReplies(prev => ({ ...prev, [commentKey]: value }))}
               onResponseSave={(commentKey) => handleSaveReply(commentKey)}
               onMarkAsOK={(commentKey) => handleCustomerMarkAsOK(commentKey)}
               onUndoMarkOK={(commentKey) => handleCustomerUndoMarkAsOK(commentKey)}
               onSendAllResponses={handleSendAllReplies}
               hasUnsavedChangesInResponse={(commentKey) => {
                 const newVal = newReplies[commentKey] || '';
                 const savedVal = savedReplies[commentKey] || '';
                 return newVal.trim() !== savedVal.trim();
               }}
               viewMode="customer"
             />
           );
         })()}

        {/* Skiss-sektion (BK-skiss PDF om den finns i Underlag) — placerad ovan Planerad projektstart.
            Döljs när estimatet är inbäddat i kundportalen (MyProjects) som redan har en egen
            "Underlag"-sektion, så kunden inte ser Underlag två gånger. */}
        {!lasLage && !hideSkiss && procurementData && (
          <SkissSection
            procurementData={procurementData}
            currentUser={user}
          />
        )}

        {/* Planerad projektstart – Offert-stil expanderbar sektion */}
        {(estimatData?.projectInfo?.önskadStart || estimatData?.projectInfo?.workDays) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <button
              onClick={() => setExpandedProjektstart(!expandedProjektstart)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg"
            >
              <h2 className="text-lg font-semibold text-gray-800">Planerad projektstart</h2>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedProjektstart ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedProjektstart && (
              <div className="px-6 pb-6">
                <p className="text-sm text-gray-600 mb-4">Detta är den planerade tidsramen för ert projekt baserat på era önskemål och vår planering.</p>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  {estimatData?.projectInfo?.önskadStart && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-green-700">Startdatum</p>
                      <p className="text-base text-gray-700">
                        {estimatData.projectInfo.önskadStart}
                      </p>
                    </div>
                  )}

                  {estimatData?.projectInfo?.workDays && (
                    <div className={estimatData?.projectInfo?.önskadStart ? "mb-0" : "mb-3"}>
                      <p className="text-sm font-medium text-green-700">Antal arbetsdagar</p>
                      <p className="text-base text-gray-700">
                        {(() => {
                          const workDays = estimatData.projectInfo.workDays;
                          const workDaysStr = String(workDays);

                          // Kolla om det är ett spann (t.ex. "80-100")
                          if (workDaysStr.includes('-') || workDaysStr.includes('–')) {
                            return `${workDays} dagar`;
                          }

                          const days = parseInt(workDaysStr, 10);
                          if (isNaN(days)) {
                            return `${workDays} dagar`;
                          }

                          // Beräkna veckor eller månader
                          if (days > 30) {
                            // Visa månader (20 arbetsdagar per månad)
                            const months = Math.round(days / 20);
                            return `${days} dagar (ca ${months} ${months === 1 ? 'månad' : 'månader'})`;
                          } else {
                            // Visa veckor (5 arbetsdagar per vecka)
                            const weeks = Math.round(days / 5);
                            return `${days} dagar (ca ${weeks} ${weeks === 1 ? 'vecka' : 'veckor'})`;
                          }
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Översikt – Offert-stil expanderbar sektion */}
        {(() => {
          const s = sectionTotals;
          const rows = [];

          // Använd *InklBaspaket-värden som inkluderar proportionellt fördelat baspaket.
          // Kategorier utan belopp utelämnas medvetet — översikten summerar pengar,
          // och preliminära moment redovisas i stället i kategorilistan nedan.
          if (s.projekteringInklBaspaket > 0) {
            rows.push({ label: 'Projektering', value: s.projekteringInklBaspaket });
          }

          if (s.byggoffertInklBaspaket > 0 || s.etableringInklBaspaket > 0 || s.byggoffertROT > 0) {
            // Byggoffert inkl. etablering/avfall/admin som en post
            rows.push({ label: 'Byggoffert', value: s.byggoffertInklBaspaket + s.etableringInklBaspaket });
          }

          if (s.sidoentreprenorerInklBaspaket > 0) {
            rows.push({ label: 'Sidoentreprenörer/specialister', value: s.sidoentreprenorerInklBaspaket });
          }

          if (s.inkopFastInredningInklBaspaket > 0) {
            rows.push({ label: 'Inköp fast inredning', value: s.inkopFastInredningInklBaspaket });
          }

          // Total före ROT: använd vatBase som redan är korrekt beräknad
          const totalExclRot = s.vatBase;
          const customerTotal = totalExclRot - s.byggoffertROT;
          const vatAmount = Math.round(s.vatBase * (25 / 125));

          if (rows.length === 0) return null;

          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <button
                onClick={() => setExpandedOversikt(!expandedOversikt)}
                className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg"
              >
                <h2 className="text-lg font-semibold text-gray-800">Översikt</h2>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedOversikt ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedOversikt && (
                <div className="px-6 pb-6">
                  {/* Nämn ROT bara när det faktiskt finns ett avdrag att visa.
                      Samma villkor som ROT-raden nedan. */}
                  <p className="text-sm text-gray-600 mb-4">
                    {s.byggoffertROT > 0
                      ? 'Här ser ni en sammanställning av estimatet per del. Beloppen inkluderar moms. ROT‑avdrag visas separat och "Estimerat att betala" är totalsumman efter ROT.'
                      : 'Här ser ni en sammanställning av estimatet per del. Beloppen inkluderar moms.'}
                  </p>

                  <div className="space-y-2">
                    {rows.map((r, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className={`text-sm text-gray-600 ${r.indent ? 'italic ml-4' : ''}`}>{r.label}</span>
                        <span className={`font-medium tabular-nums ${r.value < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {r.value.toLocaleString('sv-SE')} kr
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800">Total</span>
                      <span className="font-semibold text-gray-800 tabular-nums">{totalExclRot.toLocaleString('sv-SE')} kr</span>
                    </div>
                    {s.byggoffertROT > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-600">ROT‑avdrag{s.rotAntal > 0 ? ` (${s.rotAntal} st)` : ''}</span>
                        <span className="text-sm text-green-600 tabular-nums">-{s.byggoffertROT.toLocaleString('sv-SE')} kr</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className="font-semibold text-leadme-pink">Estimerat att betala</span>
                      <span className="font-semibold text-leadme-pink tabular-nums">{customerTotal.toLocaleString('sv-SE')} kr</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">(varav 25% moms)</span>
                      <span className="text-sm text-gray-600 tabular-nums">{vatAmount.toLocaleString('sv-SE')} kr</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Innehåll – Offert-stil expanderbar sektion (expanderad som default) */}
        {estimatData?.estimatKategorier && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <button
              onClick={() => setExpandedInnehall(!expandedInnehall)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg"
            >
              <h2 className="text-lg font-semibold text-gray-800">Innehåll</h2>
              <svg
                className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedInnehall ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedInnehall && (
              <div className="px-6 pb-6">
                <p className="text-sm text-gray-600 mb-4">Nedan ser ni hur vi har estimerat priser för ert projekt. Ni kan också lämna feedback på enskilda rader vid behov.</p>

                {/* Expandera/Minimera alla-knapp */}
                {(() => {
                  const keysToControl = Object.entries(estimatData.estimatKategorier)
                    .filter(([categoryKey]) => categoryKey !== 'inredningsarkitekt')
                    // Kräv bara att posten är vald, inte att den har kvantitet.
                    // Preliminära moment har quantity 0 och renderas som
                    // "preliminärt" — se EstimatCategoryTable.
                    .filter(([, categoryData]) => Object.values(categoryData || {}).some((it) => it && it.selected))
                    .map(([categoryKey]) => categoryKey);
                  const allOpen = keysToControl.length > 0 && keysToControl.every((k) => Boolean(expandedSections[k]));
                  return (
                    <div className="mb-4 flex gap-2">
                      <button
                        aria-label={allOpen ? "Minimera alla sektioner" : "Expandera alla sektioner"}
                        title={allOpen ? "Minimera alla" : "Expandera alla"}
                        className="px-3 py-2 text-sm rounded bg-leadme-pink text-white hover:bg-leadme-pink-dark transition-colors"
                        onClick={() => {
                          try {
                            const shouldExpand = !allOpen;
                            const next = Object.fromEntries(keysToControl.map((k) => [k, shouldExpand]));
                            setExpandedSections(next);
                            requestAnimationFrame(() => {
                              document.querySelectorAll('details[data-estimat-category]')?.forEach((el) => { el.open = shouldExpand; });
                            });
                          } catch (_) {}
                        }}
                      >
                        {allOpen ? 'Minimera alla' : 'Expandera alla'}
                      </button>
                    </div>
                  );
                })()}

                {/* Kategorier */}
                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                  {Object.entries(estimatData.estimatKategorier)
                    .filter(([categoryKey]) => categoryKey !== 'inredningsarkitekt')
                    // Se kommentaren ovan: en kategori med enbart preliminära
                    // moment ska synas, inte döljas
                    .filter(([, categoryData]) => Object.values(categoryData || {}).some((it) => it && it.selected))
                    .map(([categoryKey, categoryData]) => (
                      <details
                        key={categoryKey}
                        className="group"
                        data-estimat-category={categoryKey}
                        open={Boolean(expandedSections[categoryKey])}
                        onToggle={(e) => {
                          const isOpen = e.target.open;
                          const sectionName = ({
                            projektering: 'Projektering',
                            byggoffert: 'Byggoffert generalentreprenör',
                            sidoentreprenorer: 'Sidoentreprenörer/specialister',
                            inkopFastInredning: 'Inköp fast inredning'
                          }[categoryKey] || categoryKey);

                          // Logga sektion-expansion/kollaps
                          logActivity('section_expanded', {
                            section: sectionName,
                            expanded: isOpen
                          });

                          // Uppdatera state
                          setExpandedSections(prev => ({
                            ...prev,
                            [categoryKey]: isOpen
                          }));
                        }}
                      >
                        <summary className="flex items-center justify-between cursor-pointer px-4 py-4 select-none bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200 group-open:border-b-0">
                          <span className="text-base font-medium text-gray-800">
                            {({
                              projektering: 'Projektering',
                              byggoffert: 'Byggoffert generalentreprenör',
                              sidoentreprenorer: 'Sidoentreprenörer/specialister',
                              inkopFastInredning: 'Inköp fast inredning'
                            }[categoryKey] || categoryKey)}
                          </span>
                          <svg
                            className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </summary>
                        <div className="bg-white">
                          <EstimatCategoryTable
                            categoryKey={categoryKey}
                            categoryData={categoryData}
                            hideHeader={true}
                            comments={comments}
                            commentsRaw={commentsRaw}
                            estimatData={estimatData}
                            showComments={showComments}
                            toggleCommentField={toggleCommentField}
                            handleCommentChange={handleCommentChange}
                            handleSaveComment={handleSaveComment}
                            handleDeleteComment={handleDeleteComment}
                            handleSaveByggkonsultComment={handleSaveByggkonsultComment}
                            handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                            isCustomerView={isCustomerView}
                            lasLage={arTidigareVersion || jamforelseLage}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isSavedLocally={isSavedLocally}
                            logActivity={logActivity}
                          />
                        </div>
                      </details>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* NewFeedbackBanner visas nu högst upp i content-sektionen */}

        {/* Åtgärdsknappar / Feedback - döljs i presentationsläge */}
        {!lasLage && (() => {
          const hasCustomerFeedback = hasAnyPublishedCustomerComment(estimatData?.customerResponse?.comments);
          const anyConsultantResponse = Boolean(estimatData?.byggkonsultResponses && Object.keys(estimatData.byggkonsultResponses).length > 0);
          const isApproved = estimatData?.status === 'approved' || estimatData?.customerResponse?.action === 'approved';


          // Visa feedback-panelen ENDAST om kunden aldrig skickat feedback tidigare
          // Per-rad draft-flagga är källan till sanning — autosparade utkast ska INTE räknas
          const hasAnyCustomerComments = hasAnyPublishedCustomerComment(estimatData?.customerResponse?.comments);
          
          // Visa inte feedback-sektionen om estimatet är godkänt (särskilt via byggkonsult)
          const isApprovedByConsultant = estimatData?.approvedByConsultant;
          
          if (!feedbackSent && !hasCustomerFeedback && !anyConsultantResponse && !hasAnyCustomerComments && !isApproved && !isApprovedByConsultant) {
          return (
            <FeedbackSubmitForm
              title={(hasCustomerFeedback || anyConsultantResponse) ? 'Ny feedback' : 'Vad tycker ni om estimatet?'}
              description={(hasCustomerFeedback || anyConsultantResponse) ? 
                'Ni kan skicka ytterligare feedback eller frågor om estimatet nedan.' : 
                'Vi vill gärna ha er feedback på estimatet. Kommentera gärna enskilda rader ovan eller ge en övergripande kommentar nedan. Ni kan också begära en genomgång via Teams.'
              }
              overallFeedback={overallFeedback}
              onOverallFeedbackChange={(e) => setOverallFeedback(e.target.value)}
              onOverallFeedbackBlur={() => {
                const text = overallFeedback?.trim();
                if (text && text.length > 0) {
                  logActivity('comment_draft', {
                    section: 'Övergripande feedback',
                    draftLength: text.length,
                    // Hela texten: aktivitetsloggen är enda backupen om feedbacken tappas
                    draftText: text
                  });
                  handleSaveOverallFeedback();
                }
              }}
              budgetFeedback={budgetFeedback}
              onBudgetFeedbackChange={(e) => setBudgetFeedback(e.target.value)}
              onBudgetFeedbackBlur={() => {
                const text = budgetFeedback?.trim();
                if (text && text.length > 0) {
                  logActivity('comment_draft', { section: 'Budget', draftLength: text.length, draftText: text });
                  handleSaveBudgetFeedback();
                }
              }}
              prioriteringFeedback={prioriteringFeedback}
              onPrioriteringFeedbackChange={(e) => setPrioriteringFeedback(e.target.value)}
              onPrioriteringFeedbackBlur={() => {
                const text = prioriteringFeedback?.trim();
                if (text && text.length > 0) {
                  logActivity('comment_draft', { section: 'Prioritering', draftLength: text.length, draftText: text });
                  handleSavePrioriteringFeedback();
                }
              }}
              showMeetingRequest={true}
              meetingSlots={meetingSlots}
              onOpenMeetingModal={() => {
                setWantsMeeting(true);
                setShowMeetingModal(true);
              }}
              onSubmit={() => handleEstimatAction('feedback')}
              submitButtonText={(hasCustomerFeedback || anyConsultantResponse) ? '💬 Skicka ny feedback' : '💬 Skicka feedback'}
              submitting={submitting}
              requireMeetingSlots={wantsMeeting}
              meetingTitle="Begär genomgång via Teams"
              meetingDescription="Vi bokar ett kort möte för att gå igenom estimatet och svara på frågor."
            />
          );
          }
          
          // Om feedback redan skickats tidigare - visa ingenting här (Feedback-sektionen visas redan ovan)

          // Efter feedback skickats - visa ingenting här (Feedback-sektionen visas redan ovan)
        })()}

        {/* CTA-sektion för godkänn/tacka nej - döljs i presentationsläge */}
        {!lasLage && isCustomerView && (() => {
          const isApproved = (estimatData?.status === 'approved') || (estimatData?.customerResponse?.status === 'approved' || estimatData?.customerResponse?.action === 'approved');
          const isDeclined = (estimatData?.status === 'declined') || (estimatData?.customerResponse?.status === 'declined' || estimatData?.customerResponse?.action === 'declined');

          // Kontrollera om en genomgång har BOKATS (inte bara begärts)
          const hasMeetingBooked = Boolean(
            estimatData?.estimatFollowup?.teamsJoinUrl ||
            estimatData?.estimatFollowup?.meetingAt ||
            estimatData?.estimatFollowup?.selectedSlot
          );

          return (
            <div id="nasta-steg-section">
              <EstimatActionSection
                isApproved={isApproved}
                isDeclined={isDeclined}
                hasMeetingBooked={hasMeetingBooked}
                isCustomerView={isCustomerView}
                submitting={submitting}
                onApprove={() => handleEstimatAction('approved')}
                onDecline={() => handleEstimatAction('declined')}
              />
            </div>
          );
        })()}

        {/* Uppföljningsanteckningar - Endast synligt för byggkonsulter i presentationsläge */}
        {isPresentationMode && user?.role === 'byggkonsult' && (
          <FollowupNotesSection
            followupNotes={followupNotes}
            followupNotesSaved={followupNotesSaved}
            savingFollowupNotes={savingFollowupNotes}
            onNotesChange={(e) => {
              setFollowupNotes(e.target.value);
              setFollowupNotesSaved(false);
            }}
            onSave={saveFollowupNotes}
          />
        )}

        {/* Byggkonsult godkännande-knapp */}
        {!isCustomerView && (
          <ConsultantApprovalButton
            estimatData={estimatData}
            submitting={submitting}
            onApprove={handleApproveClick}
          />
        )}
      </div>


      {/* Confirmation Modal för osparade kommentarer */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAction}
        title="Osparade kommentarer"
        message="Du har osparade kommentarer som inte kommer att skickas. Vill du fortsätta ändå?"
        subMessage='Klicka "Avbryt" för att gå tillbaka och spara dina kommentarer först.'
        confirmText="Fortsätt ändå"
        cancelText="Avbryt"
        variant="warning"
      />

      {/* Meeting times modal */}
      <MeetingBookingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        meetingSlots={meetingSlots}
        onSlotChange={setMeetingSlots}
        availableSlots={generateMeetingOptions()}
        maxSlots={3}
        minSlots={2}
      />

      {/* Versioner sida vid sida, i läsläge — som kundens offertjämförelse */}
      {visaJamforelse && estimatData?.senasteVersion && (
        <VersionsJamforelse
          versioner={[
            { ...estimatData.senasteVersion, senaste: true },
            ...(estimatData.tidigareVersioner || [])
          ]}
          renderVersion={(id) => (
            <EstimatViewInnehall
              embedded
              isPresentationMode
              jamforelseLage
              hideSkiss
              estimatIdOverride={id}
            />
          )}
          onClose={() => setVisaJamforelse(false)}
        />
      )}

      {/* Bekräftelsedialog för godkännande åt kund */}
      <ConfirmationModalCommon
        isOpen={showApproveConfirmModal}
        onClose={() => setShowApproveConfirmModal(false)}
        onConfirm={confirmApprove}
        title="Godkänn estimat åt kund"
        message="Är du säker på att du vill godkänna estimatet åt kunden? Detta kommer att uppdatera statusen till 'Godkänt estimat' och skicka en notifikation till kunden."
        confirmText="Godkänn estimat"
        cancelText="Avbryt"
        type="success"
        isLoading={submitting}
      />

    </div>
  );
};

/**
 * Håller reda på vilken version kunden tittar på.
 *
 * useEstimatView hämtar bara en gång per montering, så ett versionsbyte
 * monterar om innehållet via key i stället för att byta id under en levande
 * hook — annars hade utkast och kommentarer från en version följt med till nästa.
 */
const EstimatView = (props) => {
  const { estimatId } = useParams();
  const [tidigareVersionId, setTidigareVersionId] = useState(null);

  // Byter föräldern estimat (t.ex. ett annat spår i MyProjects) gäller senaste igen
  const grundId = props.estimatIdOverride || estimatId;
  useEffect(() => { setTidigareVersionId(null); }, [grundId]);

  return (
    <EstimatViewInnehall
      key={tidigareVersionId || 'senaste'}
      {...props}
      estimatIdOverride={tidigareVersionId || props.estimatIdOverride}
      onVisaVersion={setTidigareVersionId}
    />
  );
};

export default EstimatView;
